/**
 * Platform Settings Context
 * Provides platform-wide settings to all components
 * Fetches settings from public API endpoint (no auth required)
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import apiClient, { getErrorMessage } from '../api/client';

export interface PlatformSettings {
  maintenance_mode: boolean;
  maintenance_message: string;
  allow_registration: boolean;
  enable_ai_features: boolean;
  enable_voice_control: boolean;
  enable_kiosk_mode: boolean;
  max_upload_size_mb: number;
  default_user_role: string;
  session_timeout_minutes: number;
  timezone: string;
  /**
   * Expert option: display Mold Growth Index in scientific VTT scale (0-6).
   * Default (false): show normalized % risk throughout the UI.
   */
  show_mgi_vtt: boolean;
  /** Default temperature display unit; users can override on their profile. */
  temperature_unit: 'F' | 'C';
}

interface PlatformSettingsContextType {
  settings: PlatformSettings | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const defaultSettings: PlatformSettings = {
  maintenance_mode: false,
  maintenance_message: "We're currently performing scheduled maintenance. Please check back soon.",
  allow_registration: false,
  enable_ai_features: true,
  enable_voice_control: true,
  enable_kiosk_mode: true,
  max_upload_size_mb: 10,
  default_user_role: 'tenant_user',
  session_timeout_minutes: 60,
  timezone: 'UTC',
  show_mgi_vtt: false,
  temperature_unit: 'F',
};

const PlatformSettingsContext = createContext<PlatformSettingsContextType>({
  settings: defaultSettings,
  isLoading: false,
  error: null,
  refetch: async () => {},
});

const CACHE_KEY = 'neimus360_platform_settings';
const CACHE_TTL = 60 * 1000; // 1 minute cache

export function PlatformSettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      // Check cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          setSettings(data);
          setIsLoading(false);
          return;
        }
      }

      // Audit Finding #H10: previously a hardcoded `/api/v1/...` fetch
      // bypassed VITE_API_URL (breaking on mobile/Capacitor where the
      // backend isn't same-origin) and skipped apiClient's refresh/error
      // interceptors. Route the call through apiClient so the base URL
      // and auth retry logic match every other request.
      try {
        const response = await apiClient.get<PlatformSettings>('/settings/platform/public');
        setSettings(response.data);
        setError(null);

        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }));
      } catch (apiError) {
        setSettings(defaultSettings);
        setError(getErrorMessage(apiError));
      }
    } catch (e) {
      console.warn('Failed to fetch platform settings:', e);
      setSettings(defaultSettings);
      setError('Failed to fetch platform settings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    
    // Refetch periodically
    const interval = setInterval(fetchSettings, CACHE_TTL);
    const handlePlatformSettingsUpdate = () => {
      localStorage.removeItem(CACHE_KEY);
      fetchSettings();
    };
    window.addEventListener('neimus360-platform-settings-updated', handlePlatformSettingsUpdate);
    return () => {
      clearInterval(interval);
      window.removeEventListener('neimus360-platform-settings-updated', handlePlatformSettingsUpdate);
    };
  }, []);

  return (
    <PlatformSettingsContext.Provider value={{ settings, isLoading, error, refetch: fetchSettings }}>
      {children}
    </PlatformSettingsContext.Provider>
  );
}

/**
 * Hook to access platform settings
 */
export function usePlatformSettings() {
  const context = useContext(PlatformSettingsContext);
  if (!context) {
    throw new Error('usePlatformSettings must be used within PlatformSettingsProvider');
  }
  return context;
}

/**
 * Hook to check if a feature is enabled
 */
export function useFeatureEnabled(feature: 'ai' | 'voice' | 'kiosk'): boolean {
  const { settings } = usePlatformSettings();
  if (!settings) return true; // Default to enabled while loading
  
  switch (feature) {
    case 'ai':
      return settings.enable_ai_features;
    case 'voice':
      return settings.enable_voice_control;
    case 'kiosk':
      return settings.enable_kiosk_mode;
    default:
      return true;
  }
}

/**
 * Hook to check maintenance mode
 */
export function useMaintenanceMode(): { isMaintenanceMode: boolean; message: string } {
  const { settings } = usePlatformSettings();
  return {
    isMaintenanceMode: settings?.maintenance_mode ?? false,
    message: settings?.maintenance_message ?? "We're currently performing scheduled maintenance.",
  };
}

/**
 * Hook to check if registration is allowed
 */
export function useRegistrationAllowed(): boolean {
  const { settings } = usePlatformSettings();
  return settings?.allow_registration ?? false;
}

/**
 * Hook to get max upload size
 */
export function useMaxUploadSize(): number {
  const { settings } = usePlatformSettings();
  return settings?.max_upload_size_mb ?? 10;
}

